import type {EventMessage, EventPayloads} from "../IntegrationEventTypes.ts";
import {EventType} from "../IntegrationEventTypes.ts";
import {IntegrationEventRouter} from "../IntegrationEventRouter.ts";
import {registerDeviceEventHandlers} from "../DeviceEventHandlers.ts";
import {MonitorController, MonitorPoll} from "./MonitorController.ts";
import {TizenSignagePanel, type SignagePanel} from "./SignagePanel.ts";
import {ScreenCapture} from "./screenshot/ScreenCapture.ts";
import {BUNDLE_VERSION} from "../../utils/BundleVersion.ts";
import {stripUndefined, type ClientDeviceInfo} from "../../utils/DeviceInfo.ts";

/** What this build may do on a Samsung panel. A capability is on only when it is enabled here AND the panel exposes the call. */
export interface SamsungFeatures {
    reboot: boolean;
    screenshot: boolean;
    monitor: boolean;
    audit: boolean;
    policy: boolean;
}

/**
 * Go/no-go per capability from the S0 spike on a real panel. Flip these one at a time, in order:
 * reboot, screenshot, monitor, policy/audit. A missing capability reports `false`; never fake it.
 */
export const SAMSUNG_FEATURES: Readonly<SamsungFeatures> = Object.freeze({
    reboot: true,
    screenshot: true,
    monitor: false,
    audit: false,
    policy: false,
});

/**
 * Remote buttons (Tizen key names) replayed as the keyboard key that does the same thing elsewhere.
 * Blue = F10 (settings, status bar), Yellow = F3 (status bar only). Red is already Enter in Keyboard.vue.
 */
export const REMOTE_KEYS: Readonly<Record<string, string>> = Object.freeze({
    ColorF3Blue: "F10",
    ColorF2Yellow: "F3",
});

type RequestIds = {requestId: string; deviceId: string};

/**
 * Samsung Smart Signage (Tizen / SSSP). There is no separate shell process: requests run in-process
 * against the panel's B2B APIs and their answers go through the same router as Tauri's, so the
 * device:* handlers are shared. The widget is updated by the panel's URL Launcher, not in-app.
 */
export class SamsungManager {
    private readonly router = new IntegrationEventRouter();
    private initialized = false;
    private active = false;
    private poll: MonitorPoll | null = null;
    private removeKeyListener: (() => void) | null = null;
    private readonly screen: ScreenCapture;
    private readonly monitor: MonitorController;

    constructor(
        private readonly panel: SignagePanel = new TizenSignagePanel(),
        private readonly features: Readonly<SamsungFeatures> = SAMSUNG_FEATURES,
    ) {
        this.screen = new ScreenCapture(panel);
        this.monitor = new MonitorController(panel);
    }

    public async init(): Promise<void> {
        if (this.initialized) return;
        this.initialized = true;
        this.active = this.panel.isPresent();

        if (!this.active) {
            console.info("[SamsungManager] Tizen not detected; skipping init.");
            return;
        }

        await this.panel.loadApis();

        registerDeviceEventHandlers((type, handler) => this.on(type, handler), "SamsungManager");
        this.mapRemoteKeys();

        if (this.capabilities().monitor) {
            this.poll = new MonitorPoll(this.monitor, (status) => this.dispatch(EventType.MonitorStatus, status));
            this.poll.start();
        }
    }

    public isAvailable(): boolean {
        return this.active;
    }

    /** The installed widget's version, like Tauri reports its shell's; the bundle version when Tizen doesn't say. */
    public getVersion(): string | null {
        return this.panel.getAppVersion() ?? BUNDLE_VERSION;
    }

    /**
     * What the panel knows about itself for the authenticate payload. Overrides the user-agent guess:
     * the Tizen engine's UA says "Linux" and carries no MAC, and the socket's IP is the proxy's, not the panel's.
     */
    public deviceInfo(): ClientDeviceInfo {
        if (!this.active) return {};
        return stripUndefined({
            os: "Tizen",
            os_version: this.panel.getPlatformVersion() ?? undefined,
            mac_address: this.panel.getMacAddress() ?? undefined,
            ip_address: this.panel.getIpAddress() ?? undefined,
        });
    }

    public capabilities(): SamsungFeatures {
        const has = this.panel.availability();
        return {
            reboot: this.features.reboot && has.reboot,
            screenshot: this.features.screenshot && has.captureScreen && has.readFile,
            monitor: this.features.monitor && has.panelMute,
            audit: this.features.audit,
            policy: this.features.policy && has.captureScreen && has.readFile,
        };
    }

    public on<T extends EventType>(type: T, handler: (message: EventMessage<T>) => void): () => void {
        return this.router.on(type, handler);
    }

    /** Client → panel notifications, answered in-process. ScreenshotPolicy feeds the scheduler once `policy` is go. */
    public async emit<T extends EventType>(type: T, payload: EventPayloads[T]): Promise<void> {
        if (!this.active) return;
        if (type === EventType.Ready) {
            const ready = payload as EventPayloads[EventType.Ready];
            this.dispatch(EventType.ReadyAck, {frontendVersion: ready.version, backendVersion: this.getVersion() ?? undefined});
        }
    }

    public async request<T extends EventType>(type: T, payload: EventPayloads[T]): Promise<void> {
        if (!this.active) return;
        const {requestId, deviceId} = payload as RequestIds;
        const ids = {requestId, deviceId};

        switch (type) {
            case EventType.RequestStatus:
                this.dispatch(EventType.StatusResponse, {...ids, status: "online"});
                return;
            case EventType.RebootRequest:
                return this.reboot(ids);
            case EventType.ScreenshotRequest:
                return this.screenshot(ids);
            case EventType.MonitorRequest: {
                const {cmd, data} = payload as EventPayloads[EventType.MonitorRequest];
                return this.runMonitor(ids, cmd, data);
            }
            case EventType.AuditSend:
                // The capture store's Offloader answers this once `audit` is go.
                this.dispatch(EventType.AuditResponse, {...ids, success: false, uploaded: 0, error: "unsupported"});
                return;
        }
    }

    public async dispose(): Promise<void> {
        this.poll?.stop();
        this.poll = null;
        this.removeKeyListener?.();
        this.removeKeyListener = null;
        this.router.clear();
        this.active = false;
        this.initialized = false;
    }

    /** Replays each registered remote key as its keyboard key on the same target, so the UI's own key handling stays the only one. */
    private mapRemoteKeys(): void {
        const keyByCode = new Map<number, string>();
        for (const [name, key] of Object.entries(REMOTE_KEYS)) {
            const code = this.panel.registerRemoteKey(name);
            if (code != null) keyByCode.set(code, key);
        }
        if (keyByCode.size === 0) return;
        const onKey = (event: KeyboardEvent) => {
            const key = keyByCode.get(event.keyCode);
            if (key === undefined) return;
            event.preventDefault();
            event.stopImmediatePropagation();
            (event.target ?? window).dispatchEvent(new KeyboardEvent("keydown", {key, bubbles: true, cancelable: true}));
        };
        window.addEventListener("keydown", onKey, true);
        this.removeKeyListener = () => window.removeEventListener("keydown", onKey, true);
    }

    private dispatch<T extends EventType>(type: T, payload: EventPayloads[T]): void {
        this.router.dispatch(type, payload);
    }

    private async reboot(ids: RequestIds): Promise<void> {
        const allowed = this.capabilities().reboot;
        this.dispatch(EventType.RebootResponse, {...ids, success: allowed});
        if (allowed) await this.panel.rebootDevice();
    }

    private async screenshot(ids: RequestIds): Promise<void> {
        if (!this.capabilities().screenshot) return;
        let bytes: Uint8Array;
        try {
            bytes = await this.screen.captureJpeg();
        } catch (e) {
            console.warn("[SamsungManager] screenshot failed", e);
            return;
        }
        this.dispatch(EventType.ScreenshotResponse, {...ids, imageBase64: new Uint8Array(bytes)});
    }

    private async runMonitor(ids: RequestIds, cmd: string, data: string): Promise<void> {
        const outcome = this.capabilities().monitor
            ? await this.monitor.run(cmd, data)
            : {result: "unsupported_display" as const};
        if (outcome.result === "ok") {
            this.dispatch(EventType.MonitorResponse, {...ids, data: "ok"});
            return;
        }
        this.dispatch(EventType.Error, {
            requestId: ids.requestId,
            code: outcome.result,
            message: outcome.result === "error" ? outcome.message : `monitor command "${cmd}" unsupported`,
        });
    }
}

export const samsungManager = new SamsungManager();
